import { graphql } from 'app/generated/gql'
import type { Client } from '@urql/core'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import type { IncomingCall } from 'app/lib/eventStream'
import { logWarn } from 'lib/logging'

const INCOMING_CALL_TTL_MS = 120_000

const INCOMING_CALLS_QUERY = graphql(`
  query IncomingCalls {
    incomingCalls {
      id
      workspaceId
      locationId
      callerPhone
      callerName
      callerCity
      callerState
      source
      trackingNumber
      trackingNumberName
      startedAt
      contactIds
    }
  }
`)

type IncomingCallsData = { incomingCalls: IncomingCall[] }

/** Holds the current workspace's short-lived ringing calls. */
export class IncomingCallStore {
  readonly callsById = observable.map<string, IncomingCall>()
  loading = false
  error: string | undefined = undefined

  private client: Client
  private workspaceId: string | undefined
  private loadVersion = 0
  private readonly expiryTimers = new Map<string, ReturnType<typeof setTimeout>>()

  constructor(client: Client) {
    this.client = client
    // makeObservable (not makeAutoObservable): annotates private fields
    // (`client`, `workspaceId`, `loadVersion`, `expiryTimers`) as non-observable,
    // which makeAutoObservable cannot do.
    makeObservable<this, 'client' | 'workspaceId' | 'loadVersion' | 'expiryTimers'>(this, {
      callsById: observable,
      loading: observable,
      error: observable,
      calls: computed,
      add: action.bound,
      remove: action.bound,
      setWorkspace: action.bound,
      reset: action.bound,
      client: false,
      workspaceId: false,
      loadVersion: false,
      expiryTimers: false,
    })
  }

  get calls(): IncomingCall[] {
    return [...this.callsById.values()].sort(
      (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
    )
  }

  add(call: IncomingCall): void {
    if (!this.workspaceId || call.workspaceId !== this.workspaceId) return

    const expiresIn = new Date(call.startedAt).getTime() + INCOMING_CALL_TTL_MS - Date.now()
    if (!Number.isFinite(expiresIn) || expiresIn <= 0) {
      this.remove(call.id)
      return
    }

    const timer = this.expiryTimers.get(call.id)
    if (timer) clearTimeout(timer)

    this.callsById.set(call.id, call)
    this.expiryTimers.set(
      call.id,
      setTimeout(() => this.remove(call.id), expiresIn),
    )
  }

  remove(id: string): void {
    const timer = this.expiryTimers.get(id)
    if (timer) clearTimeout(timer)
    this.expiryTimers.delete(id)
    this.callsById.delete(id)
  }

  setWorkspace(workspaceId: string | undefined): void {
    if (workspaceId === this.workspaceId) return
    this.reset()
    this.workspaceId = workspaceId
  }

  async load(): Promise<void> {
    const workspaceId = this.workspaceId
    if (!workspaceId) return

    const version = ++this.loadVersion
    this.loading = true
    this.error = undefined
    const result = await this.client
      .query<IncomingCallsData>(INCOMING_CALLS_QUERY, {}, { requestPolicy: `network-only` })
      .toPromise()

    if (version !== this.loadVersion || workspaceId !== this.workspaceId) return
    if (result.error) {
      runInAction(() => {
        this.loading = false
        this.error = result.error!.message
      })
      logWarn(`Failed to load incoming calls`, result.error.message)
      return
    }

    runInAction(() => {
      for (const call of result.data?.incomingCalls ?? []) {
        if (!this.callsById.has(call.id)) this.add(call)
      }
      this.loading = false
    })
  }

  reset(): void {
    this.loadVersion += 1
    for (const timer of this.expiryTimers.values()) clearTimeout(timer)
    this.expiryTimers.clear()
    this.callsById.clear()
    this.workspaceId = undefined
    this.loading = false
    this.error = undefined
  }
}
